import { ForwardedRef, forwardRef, useRef, useState } from "react";
import SearchLottie from "../Lottie/Searching";
import { XCircleIcon } from "@heroicons/react/24/solid";
import Chip from "@src/components/Chip/Chip";

import "./styles/SearchInputStyle.scss";
import { isInArray } from "@src/utils/array";
import DropdownResults from "../DropdownResults/DropdownResults";

interface IProps
	extends Omit<
		React.InputHTMLAttributes<HTMLInputElement>,
		"onClick" | "onChange"
	> {
	type?: string;
	name: string;
	placeholder?: string | undefined;
	displayClearButton?: boolean;
	disabled?: boolean;
	searchResults: (Item | null)[];
	onChange: (value: string) => void;
	onClick?: (id: Item) => void;
	isLoading: boolean;
	value?: any;
	onClickOutside?: () => void;
}
export type Item = {
	id: string;
	name: string;
};

/**
 * A universal search input. It mostly handles display. All query, data transformation and interactions must be given as props
 * @prop {string} - type - The HTML type of the input. Default: "text"
 * @prop {name}	  - name - The name of the input
 * @prop {string} - placeholder - The placeholder displayed in the input. Something like "Search for character here"
 * @prop {boolean} - displayClearButton - Whether or not to display the "x", allowing the user to instantly clear the search box
 * @prop {fn} - onChange - The function to call whenever the user types in the box. This is typically where the query will go
 * @prop {fn} - onClick	-  The function to call when the user clicks on one of the results. It will be given the target result (name and id). Default : if no function is specified, it will just add the items clicked on in a hidden text input (nameofinput-selected-items) for later use as well as display them as "tags". Useful for selecting multiple entries at once. No matter what, it will clear the search box after each click
 * @prop {[{name: string, id: string}]} - searchResults - The search results (returned by the query for ex).
 * @prop {boolean} - isLoading - Whether or not the query is loading. This will display the search icon animation if set to true
 * @prop - ref to the input where all the data is
 */

const SearchInput = forwardRef(
	(
		{
			type = "text",
			name,
			placeholder,
			displayClearButton = false,
			disabled = false,
			onChange,
			onClick,
			searchResults,
			isLoading,
			value,
			onClickOutside,
			...props
		}: IProps,
		ref: ForwardedRef<HTMLInputElement>,
	) => {
		const input = useRef<HTMLInputElement>(null);
		const hasResultsToDisplay =
			searchResults !== undefined &&
			searchResults !== null &&
			searchResults.length > 0;
		const clearSearchInput = () => {
			input.current!.value = "";
			input.current!.focus();
			onChange(input.current!.value);
		};
		const handleClick = (res: Item) => {
			if (onClick !== undefined) {
				onClick(res);
			} else {
				const newItems = [...items];
				const exists = isInArray(res.id, items);
				if (exists) {
					clearSearchInput();
					return;
				}
				newItems.push(res);
				setItems(newItems);
			}
			clearSearchInput();
			//TODO - Make sure to filter out the name alredy added
		};

		const handleDelete = (id: string) => {
			const newItems = items.filter((item) => item.id !== id);
			setItems(newItems);
		};
		const handleClickOutside = () => {
			if (onClickOutside !== undefined) {
				onClickOutside();
			} else {
				clearSearchInput();
			}
		};
		const [items, setItems] = useState<Item[]>([]);
		return (
			<>
				<div className="search-input input__wrapper">
					<input
						type={type}
						placeholder={placeholder && placeholder}
						name={name}
						aria-label={`${name}-label`}
						className="input__input"
						id={name}
						ref={input}
						disabled={disabled}
						autoComplete="off"
						onChange={(e) => {
							onChange(e.target.value);
						}}
						{...props}
					/>
					<input
						type="hidden"
						name={`${name}-selected-items`}
						value={JSON.stringify(items)}
						ref={ref}
					/>
					{isLoading ? (
						<SearchLottie /> //If this breaks, make sure your node is on v 18 ?
					) : (
						displayClearButton && (
							<button
								type="button"
								title="Clear"
								className="input__clear"
								onClick={clearSearchInput}
							>
								<XCircleIcon className="icon--xs input__clear__icon" />
							</button>
						)
					)}

					{hasResultsToDisplay && (
						<DropdownResults>
							{searchResults.map((res) => {
								if (res == null) {
									return null;
								}
								if (!isInArray(res.id, items)) {
									return (
										<li
											className="input__result__item"
											key={res.id}
											id={res.id}
											onClick={() => handleClick(res)}
										>
											{res.name}
										</li>
									);
								}
							})}
						</DropdownResults>
					)}
				</div>
				{hasResultsToDisplay && (
					<div
						id="close-overlay"
						data-testid="click-to-close"
						onClick={handleClickOutside}
					></div>
				)}
				<div className="chips__wrapper">
					{items.length > 0 &&
						items.map((item) => {
							return (
								<Chip
									key={item.id}
									id={item.id}
									displayText={item.name}
									onDelete={() => {
										handleDelete(item.id);
									}}
								/>
							);
						})}
				</div>
			</>
		);
	},
);
export default SearchInput;
